import { useCallback, useState } from 'react';
import { apiKeyManager } from '@empiriecom/mybuy-session/ApiKey';
import type { LoginResponsePayload } from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1/models/LoginResponsePayload';
import type { LegacyCredentials } from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1/models/LegacyCredentials';
import type { CaptchaData } from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1/models/CaptchaData';
import type {
  CustomerData,
  Tokens,
} from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1';
import { CredentialsTypeEnum } from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1/models/Credentials';
import { CaptchaDataFromJSON } from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1/models/CaptchaData';
import { useSWRConfig } from 'swr';
import { useRouter } from 'next/navigation';
import type { GTMEventGlycerinDisplayFormSuccess } from '@packages/tracking/src/types/events';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useLoginApi } from './LoginApi';
import { useLogout } from './useLogout';

export enum PostLoginCheck {
  BILLINGADDRESS,
  EMAIL,
  TACRECONFIRMATION, // For AGB.
}

export interface ShowCaptchaData {
  captchaData: CaptchaData | undefined;
  lastLogin: () => Promise<void>;
}

const noOp = async () => {};

export const useLogin = (onSuccess?: () => void) => {
  const { language, locale, localizeUrl } = useI18n();
  const { push } = useRouter();
  const loginApi = useLoginApi();
  const { mutate } = useSWRConfig();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [loginTokens, setLoginTokens] = useState<Tokens>();
  const [customerData, setCustomerData] = useState<CustomerData>();
  const [showCaptchaState, setShowCaptchaState] = useState<ShowCaptchaData>();
  const { logout } = useLogout();
  const dispatchGTMEvent = useTracking();
  const config = useConfig();

  const removeCaptchaData = useCallback(() => {
    setShowCaptchaState({ lastLogin: showCaptchaState?.lastLogin || noOp, captchaData: undefined });
  }, [showCaptchaState]);

  // wrap loginFn so it can be reused after captcha check if needed
  const executeLogin = useCallback(
    async (loginFn: () => Promise<LoginResponsePayload>) => {
      removeCaptchaData();
      setLoading(true);
      setError(null);

      try {
        const result = await loginFn();
        if (result.errors && result.errors.elements?.length) {
          setError(result.errors.elements[0].description);
          return;
        }

        let necessaryCheckFlags: PostLoginCheck[] = [];
        if (result.warnings?.flags?.CheckBillingAddress) {
          necessaryCheckFlags = [...necessaryCheckFlags, PostLoginCheck.BILLINGADDRESS];
        }
        if (result.warnings?.flags?.CheckEmailAddress) {
          necessaryCheckFlags = [...necessaryCheckFlags, PostLoginCheck.EMAIL];
        }
        if (result.warnings?.flags?.TACReconfirmationRequired) {
          necessaryCheckFlags = [...necessaryCheckFlags, PostLoginCheck.TACRECONFIRMATION];
        }

        if (result.customer) {
          setCustomerData(result.customer);
        }

        if (result.tokens) {
          if (necessaryCheckFlags.length) {
            // this is the case where we need to redirect to the account page to do some checks
            window.sessionStorage.setItem('masterToken', result.tokens.masterToken || '');
            window.sessionStorage.setItem('sessionToken', result.tokens.sessionToken);
            const url = `${getLocalizedPath(config.order.paths.myAccountOverview, locale)}?redirectUrl=${
              window.location.pathname
            }&checkBillingAddress=${
              necessaryCheckFlags.includes(PostLoginCheck.BILLINGADDRESS) ? 'true' : 'false'
            }&checkEmailAddress=${necessaryCheckFlags.includes(
              PostLoginCheck.EMAIL,
            )}&tacReconfirmationRequired=${necessaryCheckFlags.includes(
              PostLoginCheck.TACRECONFIRMATION,
            )}`;

            push(localizeUrl(url, language));
          } else {
            // this is happy path login was successful
            setLoginTokens(result.tokens);
            await apiKeyManager.setApiKeys(result.tokens, result.customer);
            mutate('session');
            dispatchGTMEvent<GTMEventGlycerinDisplayFormSuccess>({
              event: 'DisplayFormSuccess',
              DisplayFormSuccessData: {
                category: 'login-form',
              },
            });
            if (onSuccess) {
              onSuccess();
            }
          }
        }
      } catch (response: any) {
        if (response && response.status === 503) {
          const captchaDataFromJSON = CaptchaDataFromJSON(await response.json());
          setShowCaptchaState({
            captchaData: captchaDataFromJSON,
            lastLogin: () => executeLogin(loginFn),
          });
        } else {
          // Response is thrown from generated api - I don't want to break this behaviour
          /* istanbul ignore next */ throw response;
        }
      } finally {
        // prevent flashing of dialog after loading is finished
        setTimeout(() => {
          setLoading(false);
        }, 250);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [removeCaptchaData, language, push, localizeUrl, mutate, dispatchGTMEvent, onSuccess],
  );

  const login = ({
    email,
    password,
    remember,
  }: {
    email: string;
    password: string;
    remember?: boolean;
  }) => {
    if (loginApi) {
      executeLogin(() =>
        loginApi.login({
          ecLocale: locale,
          credentials: {
            type: CredentialsTypeEnum.Password,
            passwordCredentials: {
              email,
              password,
            },
            useAutologin: remember,
          },
        }),
      );
    } else {
      throw new Error('tried to access loginApi before it was initialized');
    }
  };

  const legacyLogin = (legacyCredentials: LegacyCredentials, remember: boolean) => {
    if (loginApi) {
      executeLogin(() =>
        loginApi.login({
          ecLocale: locale,
          credentials: {
            type: CredentialsTypeEnum.Legacy,
            legacyCredentials,
            useAutologin: remember,
          },
        }),
      );
    }
  };

  return {
    loading,
    error,
    customer: customerData,
    loginTokens,
    login,
    legacyLogin,
    logout,
    showCaptchaState,
    removeCaptchaData,
  };
};
